/**
 * Unified installments business logic operations - separated from React state
 */

import { UnifiedInstallment, InstallmentRelationship } from '@/types/unified-installments';

const INSTALLMENTS_STORAGE_KEY = 'unified_installments';
const RELATIONSHIPS_STORAGE_KEY = 'installment_relationships';

/**
 * Get storage key for profile-specific data
 */
export const getStorageKey = (profileId: string, type: 'installments' | 'relationships'): string => {
  const baseKey = type === 'installments' ? INSTALLMENTS_STORAGE_KEY : RELATIONSHIPS_STORAGE_KEY;
  return `${baseKey}_${profileId}`;
};

/**
 * Load installments from localStorage
 */
export const loadInstallmentsFromStorage = (profileId: string): UnifiedInstallment[] => {
  try {
    const storageKey = getStorageKey(profileId, 'installments');
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('[UnifiedInstallments] Error loading installments:', error);
  }
  return [];
};

/**
 * Load relationships from localStorage
 */
export const loadRelationshipsFromStorage = (profileId: string): InstallmentRelationship[] => {
  try {
    const storageKey = getStorageKey(profileId, 'relationships');
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('[UnifiedInstallments] Error loading relationships:', error);
  }
  return [];
};

/**
 * Save installments to localStorage
 */
export const saveInstallmentsToStorage = (profileId: string, installments: UnifiedInstallment[]): void => {
  try {
    const storageKey = getStorageKey(profileId, 'installments');
    localStorage.setItem(storageKey, JSON.stringify(installments));
  } catch (error) {
    console.error('[UnifiedInstallments] Error saving installments:', error);
  }
};

/**
 * Save relationships to localStorage
 */
export const saveRelationshipsToStorage = (profileId: string, relationships: InstallmentRelationship[]): void => {
  try {
    const storageKey = getStorageKey(profileId, 'relationships');
    localStorage.setItem(storageKey, JSON.stringify(relationships));
  } catch (error) {
    console.error('[UnifiedInstallments] Error saving relationships:', error);
  }
};

/**
 * Generate unique installment ID
 */
export const generateInstallmentId = (): string => {
  return `installment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a new installment with generated ID
 */
export const createInstallment = (data: Omit<UnifiedInstallment, 'id'>): UnifiedInstallment => {
  return {
    ...data,
    id: generateInstallmentId()
  };
};

/**
 * Filter installments by parent
 */
export const filterInstallmentsByParent = (
  installments: UnifiedInstallment[],
  parentId: string,
  parentType: 'debt' | 'installmentGroup'
): UnifiedInstallment[] => {
  return installments
    .filter(installment => installment.parentId === parentId && installment.parentType === parentType)
    .sort((a, b) => a.number - b.number);
};

/**
 * Find installment by transaction ID
 */
export const findInstallmentByTransaction = (
  installments: UnifiedInstallment[],
  relationships: InstallmentRelationship[],
  transactionId: string
): UnifiedInstallment | undefined => {
  const relationship = relationships.find(rel => rel.transactionId === transactionId);
  if (!relationship) return undefined;
  return installments.find(installment => installment.id === relationship.installmentId);
};

/**
 * Find transaction ID by installment
 */
export const findTransactionByInstallment = (
  relationships: InstallmentRelationship[],
  installmentId: string
): string | undefined => {
  const relationship = relationships.find(rel => rel.installmentId === installmentId);
  return relationship?.transactionId;
};

/**
 * Create a new relationship
 */
export const createRelationship = (installmentId: string, transactionId: string): InstallmentRelationship => ({
  installmentId,
  transactionId,
  createdAt: new Date().toISOString()
});

/**
 * Get updates for marking installment as paid
 */
export const getPaidUpdates = (transactionId: string, paidDate: string): Partial<UnifiedInstallment> => ({
  status: 'paid',
  paidDate,
  transactionId
});

/**
 * Get updates for marking installment as pending
 */
export const getPendingUpdates = (): Partial<UnifiedInstallment> => ({
  status: 'pending',
  paidDate: undefined,
  transactionId: undefined
});
